import React, { useState, useEffect } from "react";
import {
  TextField,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { styled } from "@mui/system";
import { RiAddCircleFill, RiDeleteBin6Line, RiEyeLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./PlainLot.css";
import Navbarr from "../Navbarr/Navbarr";
import { REACT_APP_BACKEND_SERVER_URL } from "../../config";
 
const RoundedTextField = styled(TextField)({
  maxWidth: 300,
  backgroundColor: "#f7fbff",
  borderRadius: "20px",
  // marginTop: "9rem",
  "& .MuiOutlinedInput-root": {
    borderRadius: "20px",
    paddingLeft: "16px",
    "& fieldset": {
      borderColor: "#cfd8e3",
    },
    "&:hover fieldset": {
      borderColor: "#2D4FA4", // new blue hover color
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1A2A47", // deep navy when focused
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#6b7280", // subtle gray placeholder
    opacity: 1,
  },
});
 
 
const StyledCard = styled(Card)({
  backgroundColor: "#ffffff",
  borderRadius: "18px",
  boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.08)",
  width: "200px",
  margin: "15px",
  transition: "all 0.35s ease, opacity 0.5s ease",
  opacity: 0,
  animation: "fadeInUp 0.8s ease both",
  border: "none", // removed outer line
 
  "&:hover": {
    transform: "translateY(-10px)",
    boxShadow: "0px 12px 26px rgba(0, 0, 0, 0.15)",
  }
});
 
 
 
const StyledButton = styled(Button)({
  borderRadius: "20px",
  padding: "8px 20px",
  fontSize: "16px",
});
 
const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    padding: "20px",
    borderRadius: "15px",
    backgroundColor: "#fff",
  },
});
 
function PlainLot() {
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [lotNumber, setLotNumber] = useState("");
  const [lotNumbers, setLotNumbers] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [deleteLotId, setDeleteLotId] = useState(null);
 
  const [page, setPage] = useState(1);
  const [limit] = useState(24);
  const [totalPage, setTotalPage] = useState(1);
 
  const navigate = useNavigate();
 
  useEffect(() => {
    fetchLots(1);
  }, []);
 
  const fetchLots = async (targetPage = 1) => {
    try {
      const res = await axios.get(
        `${REACT_APP_BACKEND_SERVER_URL}/api/v1/lot`,
        { params: { type: "PLAIN", page: targetPage, limit } }
      );
 
      if (Array.isArray(res.data.result)) {
        setLotNumbers(res.data.result);
        setTotalPage(res.data.totalPage);
        setPage(targetPage);
      } else {
        setLotNumbers([]);
      }
 
    } catch (err) {
      console.error("Error fetching lots:", err);
      setLotNumbers([]);
    }
  };
 
  const handleClickOpen = () => {
    setOpen(true);
  };
 
  const handleClose = () => {
    setOpen(false);
    setLotNumber("");
  };
 
  const handleLotNumberChange = (e) => {
  const value = e.target.value.replace(/\D/g, ""); // numbers only
  setLotNumber(value);
 
  };
 
  const handleSaveLotNumber = async () => {
    if (lotNumber) {
      try {
        console.log(`Saving Lot No: ${lotNumber}`);
        const response = await fetch(
          `${REACT_APP_BACKEND_SERVER_URL}/api/v1/lot/lot_info`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              lot_name: lotNumber,
              type: "PLAIN",
            }),
          }
        );
 
 
        const result = await response.json();
 
        console.log("Save Response:", result);
 
        if (response.ok) {
          const newLot = result.newLot;
            await fetchLots(1);  // always reload page 1
          console.log("New Lot:", newLot);
          setSuccessMessage("Lot created successfully!");
          setLotNumber("");
          setSearchQuery("");
          handleClose();
        } else {
          console.error("Error:", result.msg);
          setSuccessMessage(result.msg || "Error creating lot.");
        }
      } catch (error) {
        console.error("Failed to save Lot No:", error);
        setSuccessMessage("Failed to save Lot No.");
      }
    } else {
      setSuccessMessage("Lot Name is required.");
    }
  };
 
  const handleDeleteLotNumber = (index, lotId) => {
    setDeleteIndex(index);
    setDeleteLotId(lotId); // Store lot ID to delete
    setDeleteConfirmationOpen(true);
  };
 
  const confirmDelete = async () => {
    try {
      const response = await axios.put(
        `${REACT_APP_BACKEND_SERVER_URL}/api/v1/restoreLot/changetoDiactivate/${deleteLotId}`
      );
 
      if (response.status === 200) {
        const newList = lotNumbers.filter((_, i) => i !== deleteIndex);
 
        // CASE 1: Search mode → refresh search results
        if (searchQuery.trim()) {
          handleSearch(searchQuery);
        }
        else {
          // CASE 2: Pagination mode
          const isLastItemOnPage = newList.length === 0;
          const isNotFirstPage = page > 1;
 
          if (isLastItemOnPage && isNotFirstPage) {
            // go to previous page
            fetchLots(page - 1);
          } else {
            // stay on same page
            fetchLots(page);
          }
        }
 
        setDeleteConfirmationOpen(false);
        setSuccessMessage("Lot deleted successfully");
      }
      else {
        setSuccessMessage("Failed to delete lot.");
      }
    } catch (error) {
      console.error("Error deleting lot:", error);
      setSuccessMessage("Error deleting lot.");
    }
  };
 
  const handleCloseSnackbar = () => {
    setSuccessMessage("");
  };
 
  const handleCloseDeleteDialog = () => {
    setDeleteConfirmationOpen(false);
    setDeleteIndex(null);
  };
 
  const handleViewLotDetails = (lot_id, lot_name) => {
    navigate(`/plainlot/${lot_id}?lotname=${lot_name}`);
  };
 
  // const filteredLotNumbers = lotNumbers.filter((lot) =>
  //   lot.lot_name.toLowerCase().includes(searchQuery.toLowerCase())
  // );
 
  const handleSearch = async (value) => {
    setSearchQuery(value);
 
    if (!value.trim()) {
      fetchLots(1);
      return;
    }
 
    try {
      const res = await axios.get(`${REACT_APP_BACKEND_SERVER_URL}/api/v1/lot/search`, {
        params: { type: "PLAIN", query: value }
      });
 
      if (Array.isArray(res.data.result)) {
        setLotNumbers(res.data.result);
        setTotalPage(1);
        setPage(1);
      }
    } catch (err) {
      console.error("Search error:", err);
    }
  };
 
 
 
 
  return (
    <>
      <Navbarr />
      <div className="background">
        
             <Typography
            variant="h4"
            sx={{
              marginBottom: "3rem",
              textAlign: "center",
              fontWeight: "bold",
              color: "#1A2A47",
              letterSpacing: "1px",
              animation: "fadeInDown 0.8s ease"
            }}
          >
            Plain Lots
          </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mt: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2,  animation: "fadeInUp 0.8s ease 0.1s both" }}>
            <button className="plainlotBackBtn" onClick={() => navigate("/home")}><span> ←</span> Back</button>
            <RoundedTextField
              label="Search Lot Number"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search Lot No"
              fullWidth
            />
            <IconButton
              onClick={handleClickOpen}
              sx={{
                // mt: "9rem",
                color: "#1A2A47",
                border: "2px solid transparent",
                transition: "all 0.3s ease",
                "&:hover": {
                  border: "2px solid #1A2A47",
                  backgroundColor: "transparent"
                }
              }}
            >
              <RiAddCircleFill size={42} />
            </IconButton>
          </Box>
 
          <Dialog open={open} onClose={handleClose}>
            <DialogTitle style={{ fontWeight: "bold" }}>Add Lot Number</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Lot No"
                type="text"
                fullWidth
                variant="outlined"
                value={lotNumber}
                onChange={handleLotNumberChange}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Cancel</Button>
              <Button onClick={handleSaveLotNumber} color="primary" variant="contained">
                Save
              </Button>
            </DialogActions>
          </Dialog>
 
          <StyledDialog
            open={deleteConfirmationOpen}
            onClose={handleCloseDeleteDialog}
            aria-labelledby="delete-lot-number-dialog"
          >
            <DialogTitle sx={{ fontSize: "18px", fontWeight: "bold", color: "#1976d2" }}>
              Confirm Deletion
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to delete this Lot No?
              </Typography>
            </DialogContent>
            <DialogActions>
              <StyledButton onClick={handleCloseDeleteDialog} color="secondary" variant="outlined">
                Cancel
              </StyledButton>
              <StyledButton onClick={confirmDelete} color="primary" variant="contained">
                Yes, Delete
              </StyledButton>
            </DialogActions>
          </StyledDialog>
 
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              mt: 2,
              maxWidth: "100%",
              animation: "fadeInUp 0.8s ease 0.2s both"
            }}
          >
            {lotNumbers.length > 0 ? (
              lotNumbers.map((lot, index) => (
                <StyledCard key={`${lot.id}-${index}`} className={searchQuery ? "no-animation" : ""} style={searchQuery ? {} : { animationDelay: `${index * 0.06}s` }}>
                  <CardContent sx={{ textAlign: "center", backgroundColor: "#1A2A47", borderRadius: "18px 18px 0 0" }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        color: "white",
                        mb: 1,
                        fontSize: "1.2rem",
                      }}
                    >
                      {lot.lot_name}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        px: 2,
                      }}
                    >
                      <IconButton onClick={() => handleViewLotDetails(lot.id, lot.lot_name)}>
                        <RiEyeLine size={20} color="white" />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteLotNumber(index, lot.id)}>
                        <RiDeleteBin6Line size={20} color="white" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </StyledCard>
              ))
            ) : (
              <Typography variant="body1" color="rgb(36, 36, 66)" fontWeight="bold" fontSize="1.5rem">
                No Lot numbers available.
              </Typography>
            )}
              
      {searchQuery === "" && (
        <Box className="pl-pagination pl-pagination--floating">
          <Button
            disabled={page <= 1}
            onClick={() => fetchLots(page - 1)}
            className={page <= 1 ? "" : ""}
          >
            ◀ Prev
          </Button>
 
          <Typography className="pl-page-indicator" sx={{ fontWeight: "bold", color: "#1A2A47" }}>
            Page {page} of {totalPage}
          </Typography>
 
          <Button
            disabled={page >= totalPage}
            onClick={() => fetchLots(page + 1)}
          >
            Next ▶
          </Button>
        </Box>
      )}
 
 
 
 
          </Box>
 
          <Snackbar open={!!successMessage} autoHideDuration={6000} onClose={handleCloseSnackbar}>
            <Alert onClose={handleCloseSnackbar} severity={successMessage.includes("Error") ? "error" : "success"} sx={{ width: "100%" }}>
              {successMessage}
            </Alert>
          </Snackbar>
        </Box>
      </div>
    </>
  );
}
 
export default PlainLot;